import NoteSequencesContract from '../contracts/NoteSequencesContract.js';
import EthereumQuery from '../EthereumQuery.js';
import {bytes32ToNote} from './NoteConstants.js';
import {noteToBytes32} from './NoteConstants.js';
import ipfsClient from '../Ipfs.js';
import {NewNoteSequenceAggregation} from '../EthereumEvents.js';
import {NewNoteSequence} from '../EthereumEvents.js';
import Notes from 'midi/Notes.js';

const contract = new NoteSequencesContract();

const SCORE_THRESHOLD = 80;
const NOTE_SKIP_PENALTY = 5;

class ChordSearcher {
    constructor() {
    }

    convertToNotes(notes) {
        const middleC = 60;
        notes = notes
            .filter(note => note !== undefined)
            .filter(note => note.pitch !== undefined)
            .map(
            note => { return {
                note: Notes.fromTranspose(note.pitch - middleC).note,
                startTime:  note.startTime,
                endTime: note.endTime
            }});

        return notes;
    }

    getChordsFromSequence(noteSequence, chordNotes) {
        noteSequence.sort((a, b) => a.startTime - b.startTime);

        noteSequence = this.convertToNotes(noteSequence);

        let chordsFound = [];

        for (let i=0; i < noteSequence.length; i++) {
            let note = noteSequence[i];
            let chordNotesToCheck = [... chordNotes];

            // see if starting at this note in the sequence, we can find the chord
            // and with what score
            let chordFound = [];

            if (note.note !== chordNotesToCheck[0]) {
                continue;
            }

            for (let k=i; k < noteSequence.length; k++) {
                if (chordNotesToCheck.length == 0) {
                    // we've finished
                    break;
                }

                let chordNoteToCheck = chordNotesToCheck[0];
                if (chordNoteToCheck === noteSequence[k].note) {
                    chordNotesToCheck = chordNotesToCheck.slice(1);
                    chordFound.push(noteSequence[k]);

                    let newK = k;
                    for (let l=k+1; l < noteSequence.length; l++) {
                        // see how many trail
                        if (chordNoteToCheck == noteSequence[l].note) {
                            chordFound.push(noteSequence[l]);
                            newK = l;
                        } else {
                            break;
                        }
                    }
                    k = newK;

                } else {
                    // otherwise we found a bum note between the chord we want
                    // save this so we know to score it poorly
                    chordFound.push(null);
                }
            }

            if (chordNotesToCheck.length == 0) {
                chordsFound.push(chordFound);
            }
        }

        // prune the trailing nulls

        chordsFound = chordsFound.map(
            chord => this.pruneTrailingNulls(chord));

        let goodChords = chordsFound.filter(
            chord => chord.filter(note => note === null).length <= chord.length * 0.5);

        // now from the good chords just return the slices
        let slices = goodChords
            .filter(chord => chord.length > 0)
            .map(
                chord => {
                return {
                    startTime: chord[0].startTime,
                    endTime: chord[chord.length - 1].endTime,
                };
                })
            .map(
                chord => {
                    return {
                        ... chord,
                        density: this.calculateDensityForSlice(chord, noteSequence)
                    };
                });;

        let nonOverlappingSlices = [];
        for (let i in slices) {
            let currentSlice = slices[i];
            let overlaps = slices.filter(slice => currentSlice.startTime > slice.startTime &&
                                         currentSlice.endTime <= slice.endTime)
                .length > 0;

            if (!overlaps) {
                nonOverlappingSlices.push(currentSlice);
            }
        }

        return nonOverlappingSlices;
    }

    calculateDensityForSlice(chord, notes) {
        let notesInRange = notes.filter(
            note => (note.startTime >= chord.startTime &&
                     note.startTime <= chord.endTime) ||
                (note.endTime >= chord.startTime &&
                 note.endTime <= chord.endTime));

        let duration = chord.endTime - chord.startTime;
        return notesInRange.length / duration;
    }

    pruneTrailingNulls(list) {
        let l = [];
        for (let i in list) {
            if (list.slice(i).every(x => x === null)) {
                break;
            }
            l.push(list[i]);
        }

        return l;
    }

    async getNoteSequence(sampleHash) {
        let results = await new EthereumQuery()
            .from(NewNoteSequence)
            .select('noteSequenceHash')
            .where('sampleHash', sampleHash)
            .executeQuery();

        if (results.length == 0) {
            return undefined;
        }

        let noteSequenceHash = results[0].noteSequenceHash;
        return await ipfsClient.getJson(noteSequenceHash);
    }





    // returns samples -> chords + startTime, ranked by
    // their "match", i.e. how close to the chord it is
    async searchRankedNotes(notes) {
        let searchResults = await this.searchNotes(notes);

        let weightedResults = [];
        for (let i in searchResults) {
            let result = searchResults[i];
            let remainingNotes = result.remainingNotes;

            // start with score of 100 then
            // for every note of the chord that is missing penalize by
            // (1 / num Notes) * 100
            // and for every note we skip to find a note in the chord, we take
            // off 1 point
            let score = 100;

            // go through the inputed notes we are searching for
            for (let j in notes) {
                let note = notes[j];
                let slicedNotes = remainingNotes.slice(j);
                let indexOf = slicedNotes.indexOf(note);

                if (indexOf == -1) {
                    score -= (1 / notes.length) * 200;
                } else {
                    if (indexOf > 0) {
                        score -= (indexOf) * NOTE_SKIP_PENALTY;
                    }
                }
            }

            let numberOfNotes = remainingNotes.length;
            let startTime = result.startTime / 1000;
            let endTime = result.endTime / 1000;
            let density = numberOfNotes / (endTime - startTime);

            if (score < SCORE_THRESHOLD) {
                continue;
            }

            weightedResults.push({score: score, density: density, result: result});
        }

        weightedResults.sort((a,b) => a.density - b.density);

        // now given the sorted results return a mapping from sample hash to chords
        let sampleToResults = {};
        for (let i in weightedResults) {
            if (weightedResults[i].score <= SCORE_THRESHOLD) {
                continue;
            }
            let result = weightedResults[i].result;
            let sampleHash = result.sampleHash;
            let remainingNotes = result.remainingNotes;
            let startTime = result.startTime;
            let endTime = result.endTime;

            let obj = {
                startTime: startTime,
                endTime: endTime,
                remainingNotes: remainingNotes
            };
            if (sampleHash in sampleToResults) {
                sampleToResults[sampleHash].push(obj);
            } else {
                sampleToResults[sampleHash] = [obj];
            }
        }

        return sampleToResults;
    }

    async getStartTimesForSample(sample) {
        return new Promise((resolve, reject) => {
            new EthereumQuery(contract.contractAddress)
                .from(NewNoteSequenceAggregation)
                .select('startTime')
                .where('sampleHash', sample)
                .executeQuery()
                .then(results => resolve(results));
        });
    }

    searchNotes(notes) {
        return new Promise((resolve, reject) => {
            if (notes.length == 0) {
                resolve();
                return;
            }
            let bytes32Notes = notes.map(note => noteToBytes32[note]);
            let query = new EthereumQuery(contract.contractAddress)
                .from(NewNoteSequenceAggregation)
                .select('sampleHash')
                .select('noteA')
                .select('noteB')
                .select('remainingNotes')
                .select('startTime')
                .select('endTime')
                .where('noteA', bytes32Notes[0]);

            if (notes.length > 1) {
                query = query.where('noteB', bytes32Notes[1]);
            }

            query.executeQuery().then(results => resolve(results));
        });
            
        
    }
}

export default ChordSearcher;
